/**
 * Video Compositor Engine for Browser Recording.
 *
 * Solves orientation and aspect ratio mismatches across mobile and desktop devices
 * (e.g. iPhone front/back camera returning portrait 1080x1920 frames when the user
 * wants widescreen 16:9 landscape output, or desktop webcams recording vertical shorts).
 *
 * Renders video frames onto a canvas at exact target resolution and orientation
 * (16:9 Landscape: 1920x1080 / 1280x720, 9:16 Portrait: 1080x1920, 1:1 Square: 1080x1080)
 * with cover-scaling, optional rotation, and mirroring, producing a pure, correctly oriented
 * MediaStream for MediaRecorder.
 */

export function renderVideoFrameToCanvas(video, canvas, ctx, { targetW, targetH, rotDeg = 0, mirror = false }) {
  if (!video || !canvas || !ctx) return;
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return;

  const isRotated90or270 = rotDeg === 90 || rotDeg === 270;
  const effectiveVw = isRotated90or270 ? vh : vw;
  const effectiveVh = isRotated90or270 ? vw : vh;

  // Cover scale: fill the canvas completely without letterboxing or squishing
  const scale = Math.max(targetW / effectiveVw, targetH / effectiveVh);
  const drawW = vw * scale;
  const drawH = vh * scale;

  ctx.save();
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, targetW, targetH);

  // Center coordinate space on canvas
  ctx.translate(targetW / 2, targetH / 2);

  // Rotate frame first to bring sensor video into target canvas orientation
  if (rotDeg !== 0) {
    ctx.rotate((rotDeg * Math.PI) / 180);
  }

  // Mirror horizontally in camera frame (selfie / front camera)
  if (mirror) {
    ctx.scale(-1, 1);
  }

  // Draw video centered at origin
  ctx.drawImage(video, -drawW / 2, -drawH / 2, drawW, drawH);
  ctx.restore();
}

/**
 * Calculates effective rotation in degrees (0, 90, 180, 270).
 */
export function computeEffectiveRotation({
  rotationMode = "auto",
  isLandscape = true,
  videoWidth = 0,
  videoHeight = 0,
  isDeviceLandscape = false,
  screenAngle = 0,
}) {
  if (rotationMode !== "auto") {
    const parsed = Number(rotationMode);
    return isNaN(parsed) ? 0 : parsed % 360;
  }

  // Auto mode:
  // If target is landscape, but raw camera feed is portrait (e.g. 1080x1920 on iPhone)
  if (isLandscape && videoWidth > 0 && videoHeight > 0 && videoWidth < videoHeight) {
    if (isDeviceLandscape) {
      // User has physically turned phone into landscape orientation
      if (screenAngle === 270 || screenAngle === -90) {
        return 270;
      }
      return 90;
    }
    // Phone held upright in portrait, but user selected landscape: crop center 16:9
    return 0;
  }

  return 0;
}

/**
 * Creates an active video compositor instance that streams to MediaRecorder.
 */
export function createVideoCompositor({
  video,
  targetW,
  targetH,
  rotDeg = 0,
  mirror = false,
  fps = 30,
  audioTracks = [],
}) {
  if (typeof window === "undefined" || !video) return null;

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;

  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) return null;

  let currentSettings = { targetW, targetH, rotDeg, mirror };
  let isRunning = false;
  let animId = null;
  let intervalId = null;
  const rvfcSupported = typeof video.requestVideoFrameCallback === "function";

  // Initial draw immediately
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, targetW, targetH);
  if (video.videoWidth > 0 && video.videoHeight > 0) {
    renderVideoFrameToCanvas(video, canvas, ctx, currentSettings);
  }

  // Render loop
  const drawNext = () => {
    if (!isRunning) return;

    if (video.readyState >= 2 && video.videoWidth > 0) {
      renderVideoFrameToCanvas(video, canvas, ctx, currentSettings);
    }

    if (rvfcSupported) {
      animId = video.requestVideoFrameCallback(drawNext);
    } else {
      animId = requestAnimationFrame(drawNext);
    }
  };

  // Start rendering immediately
  isRunning = true;
  drawNext();

  // Background fallback interval in case rAF is throttled in tab
  intervalId = setInterval(() => {
    if (isRunning && video.readyState >= 2 && video.videoWidth > 0) {
      renderVideoFrameToCanvas(video, canvas, ctx, currentSettings);
    }
  }, Math.max(16, Math.round(1000 / (fps || 30))));

  // Create stream from canvas (supporting vendor prefixes)
  const captureMethod = canvas.captureStream || canvas.webkitCaptureStream;
  if (!captureMethod) {
    console.warn("captureStream not supported on canvas");
    isRunning = false;
    clearInterval(intervalId);
    return null;
  }

  let canvasStream;
  try {
    canvasStream = captureMethod.call(canvas, fps || 30);
  } catch (err) {
    console.warn("captureStream failed on canvas", err);
    isRunning = false;
    clearInterval(intervalId);
    return null;
  }

  // Attach audio tracks to canvas stream
  if (audioTracks && audioTracks.length > 0) {
    audioTracks.forEach((track) => {
      try {
        canvasStream.addTrack(track);
      } catch (e) {
        console.warn("Failed to add audio track to canvas stream", e);
      }
    });
  }

  return {
    canvas,
    canvasStream,
    updateSettings(newSettings) {
      currentSettings = { ...currentSettings, ...newSettings };
      if (newSettings.targetW && newSettings.targetW !== canvas.width) {
        canvas.width = newSettings.targetW;
      }
      if (newSettings.targetH && newSettings.targetH !== canvas.height) {
        canvas.height = newSettings.targetH;
      }
    },
    stop() {
      isRunning = false;
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
      }
      if (animId != null) {
        if (rvfcSupported && typeof video.cancelVideoFrameCallback === "function") {
          video.cancelVideoFrameCallback(animId);
        } else {
          cancelAnimationFrame(animId);
        }
        animId = null;
      }
      if (canvasStream) {
        canvasStream.getVideoTracks().forEach((t) => t.stop());
      }
    },
  };
}
